import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Modal from '../../components/Common/Modal';
import { showSuccess, showError, showConfirm } from '../../utils/swal';
import {
  Target, Settings, Building2, Shield, Users, CheckCircle,
  HardHat, ChevronRight, ChevronLeft, Save, Plus, Trash2,
  Pencil, Lock, Unlock, Loader2, LogOut, Info, AlertTriangle
} from 'lucide-react';
import confetti from 'canvas-confetti';

const SetupWizard = () => {
  const navigate = useNavigate();
  const { user, logout, isSetupCompleted, refreshSetupStatus } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [officeType, setOfficeType] = useState(''); // 'emlak_ofisi' | 'insaat_sirketi'
  const [defaults, setDefaults] = useState(null);

  // Adım 2: Ofis Ayarları
  const [settings, setSettings] = useState({
    data_visibility: 'private',
    default_portfolio_visibility: 'owner',
    office_commission_rate: 30,
  });

  // Adım 3: Departmanlar
  const [departments, setDepartments] = useState([]);

  // Adım 4: Departman Yetkileri
  const [permissions, setPermissions] = useState({});
  const [modules, setModules] = useState([]);

  // Adım 5: Personeller
  const [usersList, setUsersList] = useState([]);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    department_id: '',
    is_manager: false
  });

  useEffect(() => {
    // Eğer kullanıcı zaten kurulumu tamamladıysa dashboard'a at
    if (isSetupCompleted) {
      navigate('/');
    }
  }, [isSetupCompleted, navigate]);

  const handleOfficeTypeSelect = async (type) => {
    setLoading(true);
    setOfficeType(type);
    try {
      const response = await api.get(`/setup/defaults/${type}`);
      const data = response.data.data;
      setDefaults(data);
      setSettings(data.settings);
      setDepartments(data.departments);
      setPermissions(data.permissions);
      setCurrentStep(1);
    } catch (error) {
      showError('Varsayılan ayarlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setCurrentStep(prev => prev + 1);
  const prevStep = () => setCurrentStep(prev => prev - 1);

  const steps = [
    { title: 'Amaç Seçimi', icon: Target },
    { title: 'Ofis Ayarları', icon: Settings },
    { title: 'Departmanlar', icon: Building2 },
    { title: 'Yetkiler', icon: Shield },
    { title: 'Personeller', icon: Users },
    { title: 'Tamamla', icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header / Stepper */}
      <div className="bg-white border-b px-4 py-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <React.Fragment key={index}>
                  <div className="flex flex-col items-center relative z-10">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${isActive ? 'bg-primary-600 text-white ring-4 ring-primary-100' :
                      isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                      }`}>
                      {isCompleted ? <CheckCircle size={20} /> : <Icon size={20} />}
                    </div>
                    <span className={`mt-2 text-xs font-medium hidden md:block ${isActive ? 'text-primary-600' : 'text-gray-500'}`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 h-0.5 bg-gray-200 mx-2 md:mx-4 mt-5">
                      <div className={`h-full bg-primary-600 transition-all duration-500 ${isCompleted ? 'w-full' : 'w-0'}`} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 md:p-8 flex items-start justify-center overflow-y-auto">
        <div className="max-w-4xl w-full pb-24">
          {currentStep === 0 && <StepPurpose onSelect={handleOfficeTypeSelect} loading={loading} />}
          {currentStep === 1 && <StepSettings settings={settings} setSettings={setSettings} onNext={nextStep} onPrev={prevStep} />}
          {currentStep === 2 && (
            <StepDepartments
              departments={departments}
              setDepartments={setDepartments}
              permissions={permissions}
              setPermissions={setPermissions}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 3 && (
            <StepPermissions
              departments={departments}
              permissions={permissions}
              setPermissions={setPermissions}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 4 && (
            <StepUsers
              departments={departments}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 5 && (
            <StepSummary
              officeType={officeType}
              settings={settings}
              departments={departments}
              onPrev={prevStep}
              refreshSetupStatus={refreshSetupStatus}
            />
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex justify-between items-center px-8">
        <div className="flex items-center gap-2 text-gray-500">
          <Info size={16} />
          <span className="text-sm">Kurulum sırasında istediğiniz zaman geri dönebilirsiniz.</span>
        </div>
        <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={logout} icon={<LogOut size={18} />}>
          Çıkış Yap
        </Button>
      </div>
    </div>
  );
};

/* Adım 1: Amaç Seçimi */
const StepPurpose = ({ onSelect, loading }) => (
  <div className="text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
    <h1 className="text-3xl font-bold text-gray-900 mb-2">CRM'i hangi amaçla kullanacaksınız?</h1>
    <p className="text-gray-500 mb-8">İhtiyaçlarınıza en uygun varsayılan ayarları yapılandırmak için ofis tipinizi seçin.</p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <button
        onClick={() => onSelect('emlak_ofisi')}
        disabled={loading}
        className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-primary-500 hover:shadow-xl transition-all duration-300 text-left relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
          <Building2 size={120} />
        </div>
        <div className="w-16 h-16 bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
          <Building2 size={32} />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Emlak Ofisi</h3>
        <p className="text-gray-500">Gayrimenkul alım-satım, kiralama, portföy yönetimi ve danışman takibi için optimize edilmiştir.</p>
        <div className="mt-6 flex items-center text-primary-600 font-semibold">
          Seç ve Devam Et <ChevronRight size={18} className="ml-1" />
        </div>
      </button>

      <button
        onClick={() => onSelect('insaat_sirketi')}
        disabled={loading}
        className="group bg-white p-8 rounded-2xl border-2 border-gray-100 hover:border-primary-500 hover:shadow-xl transition-all duration-300 text-left relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
          <HardHat size={120} />
        </div>
        <div className="w-16 h-16 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
          <HardHat size={32} />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">İnşaat Şirketi</h3>
        <p className="text-gray-500">Proje satış yönetimi, stok takibi, şantiye personeli ve satış temsilcileri için optimize edilmiştir.</p>
        <div className="mt-6 flex items-center text-orange-600 font-semibold">
          Seç ve Devam Et <ChevronRight size={18} className="ml-1" />
        </div>
      </button>
    </div>

    {loading && (
      <div className="mt-8 flex items-center justify-center gap-2 text-primary-600">
        <Loader2 className="animate-spin" />
        <span>Varsayılan ayarlar yükleniyor...</span>
      </div>
    )}
  </div>
);

/* Adım 2: Ofis Ayarları */
const StepSettings = ({ settings, setSettings, onNext, onPrev }) => {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.put('/setup/settings', settings);
      showSuccess('Ayarlar kaydedildi.');
      onNext();
    } catch (error) {
      showError('Ayarlar kaydedilirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Ofis Ayarları</h2>

      <div className="space-y-6 mb-8">
        <Card title="Müşteri & Görüşme Veri Paylaşımı" icon={<Users size={20} className="text-blue-500" />}>
          <div className="space-y-4">
            <Select
              value={settings.data_visibility}
              onChange={(e) => setSettings({ ...settings, data_visibility: e.target.value })}
              options={[
                { value: 'private', label: 'Sadece Kendi Verilerini Görsün (Gizli)' },
                { value: 'shared', label: 'Tüm Ofis Verileri Şeffaf Olarak Görünsün' }
              ]}
            />
            <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-100 italic">
              {settings.data_visibility === 'private'
                ? "Her personel sadece kendi müşterilerini ve görüşmelerini görebilir. Yöneticiler tüm verilere erişebilir."
                : "Tüm ofis personeli birbirlerinin müşterilerini ve görüşmelerini şeffaf olarak görebilir."
              }
            </p>
          </div>
        </Card>

        <Card title="Portföy Varsayılan Görünürlüğü" icon={<Building2 size={20} className="text-purple-500" />}>
          <div className="space-y-4">
            <Select
              value={settings.default_portfolio_visibility}
              onChange={(e) => setSettings({ ...settings, default_portfolio_visibility: e.target.value })}
              options={[
                { value: 'owner', label: 'Sadece Sahibine Özel' },
                { value: 'office', label: 'Tüm Ofise Açık' }
              ]}
            />
            <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-100 italic">
              {settings.default_portfolio_visibility === 'owner'
                ? "Portföyler varsayılan olarak sadece sahibi ve yöneticiler tarafından görülebilir. Danışmanlar isterlerse portföylerini paylaşabilir."
                : "Portföyler varsayılan olarak tüm ofis personeline açıktır. Herkes tüm portföyleri görebilir ve düzenleyebilir."
              }
            </p>
          </div>
        </Card>

        <Card title="Ofis Komisyon Payı (%)" icon={<Settings size={20} className="text-orange-500" />}>
          <div className="space-y-4">
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={settings.office_commission_rate}
              onChange={(e) => setSettings({ ...settings, office_commission_rate: e.target.value })}
              placeholder="Örn: 30"
            />
            <p className="text-sm text-gray-500">
              Danışmanların gerçekleştirdiği satış ve kiralama işlemlerinden ofise kesilecek komisyon oranı.
              Örneğin %30 girilirse, her işlemden ofis %30 pay alır.
            </p>
          </div>
        </Card>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev} icon={<ChevronLeft size={18} />}>Geri</Button>
        <Button onClick={handleSubmit} loading={loading} icon={<ChevronRight size={18} />} iconPosition="right">
          Kaydet & Devam Et
        </Button>
      </div>
    </div>
  );
};

/* Adım 3: Departmanlar */
const StepDepartments = ({ departments, setDepartments, permissions, setPermissions, onNext, onPrev }) => {
  const [loading, setLoading] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = () => {
    if (!newDeptName.trim()) return;
    setDepartments([...departments, { name: newDeptName.trim(), is_new: true }]);
    setNewDeptName('');
    setIsAdding(false);
  };

  const handleDelete = (index) => {
    const dept = departments[index];
    if (dept.name === 'Yönetici') {
      showError('Yönetici departmanı silinemez.');
      return;
    }

    // Departmanı listeden kaldır
    const newDepts = departments.filter((_, i) => i !== index);
    setDepartments(newDepts);

    // İlgili yetkileri de temizle (hem name hem id key'li olabilir)
    const newPerms = { ...permissions };
    if (dept.id) delete newPerms[dept.id];
    delete newPerms[dept.name];
    setPermissions(newPerms);
  };

  const handleUpdate = (index, value) => {
    const newDepts = [...departments];
    newDepts[index].name = value;
    setDepartments(newDepts);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await api.post('/setup/departments', { departments });
      const savedDepts = response.data.data;

      // Permissions state'ini departman adı key'lerinden departman id key'lerine dönüştür
      const newPermissions = {};
      savedDepts.forEach(dept => {
        // Önce eski permissions'da departman adıyla eşleşen var mı bak
        if (permissions[dept.name]) {
          newPermissions[dept.id] = permissions[dept.name];
        }
        // Sonra eski permissions'da departman id'siyle eşleşen var mı bak (zaten id'li ise)
        else if (permissions[dept.id]) {
          newPermissions[dept.id] = permissions[dept.id];
        }
      });

      setPermissions(newPermissions);
      setDepartments(savedDepts);
      showSuccess('Departmanlar kaydedildi.');
      onNext();
    } catch (error) {
      showError(error.response?.data?.message || 'Departmanlar kaydedilirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Departmanlar</h2>
        <Button size="sm" onClick={() => setIsAdding(true)} icon={<Plus size={18} />}>Yeni Departman Ekle</Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        <table className="w-full text-left">
          <thead className="bg-gray-50 text-gray-500 text-sm uppercase">
            <tr>
              <th className="px-6 py-4 font-semibold">Departman Adı</th>
              <th className="px-6 py-4 font-semibold text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {departments.map((dept, idx) => (
              <tr key={idx} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Input
                      className="max-w-xs"
                      value={dept.name}
                      disabled={dept.name === 'Yönetici'}
                      onChange={(e) => handleUpdate(idx, e.target.value)}
                    />
                    {dept.name === 'Yönetici' && (
                      <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">Varsayılan</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  {dept.name !== 'Yönetici' && (
                    <Button
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      size="sm"
                      onClick={() => handleDelete(idx)}
                      icon={<Trash2 size={18} />}
                    />
                  )}
                </td>
              </tr>
            ))}
            {isAdding && (
              <tr className="bg-primary-50/30">
                <td className="px-6 py-4">
                  <Input
                    autoFocus
                    placeholder="Departman adı girin..."
                    value={newDeptName}
                    onChange={(e) => setNewDeptName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  />
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setIsAdding(false)}>İptal</Button>
                    <Button size="sm" onClick={handleAdd}>Ekle</Button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev} icon={<ChevronLeft size={18} />}>Geri</Button>
        <Button onClick={handleSubmit} loading={loading} icon={<ChevronRight size={18} />} iconPosition="right">
          Kaydet & Devam Et
        </Button>
      </div>
    </div>
  );
};

/* Adım 4: Departman Yetkileri */
const StepPermissions = ({ departments, permissions, setPermissions, onNext, onPrev }) => {
  const [activeDept, setActiveDept] = useState(departments[0]?.id || 0);
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await api.get('/department-permissions/modules');
        setModules(response.data.data);
      } catch (error) {
        showError('Modül listesi alınamadı.');
      } finally {
        setLocalLoading(false);
      }
    };
    fetchModules();
  }, []);

  useEffect(() => {
    if (departments.length > 0 && !activeDept) {
      setActiveDept(departments[0].id);
    }
  }, [departments, activeDept]);

  const togglePermission = (deptId, moduleKey, action) => {
    setPermissions(prev => ({
      ...prev,
      [deptId]: {
        ...prev[deptId],
        [moduleKey]: {
          ...(prev[deptId]?.[moduleKey] || {}),
          [action]: !prev[deptId]?.[moduleKey]?.[action]
        }
      }
    }));
  };

  const handleSubmit = async () => {
    // Validasyon: Yönetici hariç her departmanda en az bir yetki olmalı
    for (const dept of departments) {
      if (dept.name === 'Yönetici') continue;

      const deptPerms = permissions[dept.id];
      if (!deptPerms) {
        showError(`"${dept.name}" departmanı için hiç yetki tanımlanmamış. Lütfen en az bir yetki ekleyin.`);
        setActiveDept(dept.id);
        return;
      }

      // En az bir modülde en az bir true değer var mı kontrol et
      const hasAnyPermission = Object.values(deptPerms).some(modulePerms =>
        Object.values(modulePerms).some(val => val === true)
      );

      if (!hasAnyPermission) {
        showError(`"${dept.name}" departmanı için hiç yetki tanımlanmamış. Lütfen en az bir yetki ekleyin.`);
        setActiveDept(dept.id);
        return;
      }
    }

    setLoading(true);
    try {
      await api.post('/setup/permissions', { permissions });
      showSuccess('Yetkiler kaydedildi.');
      onNext();
    } catch (error) {
      showError(error.response?.data?.message || 'Yetkiler kaydedilirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const activeDeptData = departments.find(d => d.id === activeDept);
  const isManagerDept = activeDeptData?.name === 'Yönetici';

  if (localLoading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-primary-600" /></div>;

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Departman Yetkileri</h2>

      <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
        {departments.map((dept) => (
          <button
            key={dept.id}
            onClick={() => setActiveDept(dept.id)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${activeDept === dept.id
              ? 'bg-primary-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
          >
            {dept.name}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        {isManagerDept ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield size={32} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Tam Yetki</h3>
            <p className="text-gray-500">Yönetici departmanı tüm modüllerde tam yetkiye sahiptir ve bu değiştirilemez.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 font-semibold">Modül</th>
                  <th className="px-2 py-3 font-semibold text-center">Gör.</th>
                  <th className="px-2 py-3 font-semibold text-center">Ekle</th>
                  <th className="px-2 py-3 font-semibold text-center">Düzen.</th>
                  <th className="px-2 py-3 font-semibold text-center">Sil</th>
                  <th className="px-2 py-3 font-semibold text-center">Durum</th>
                  <th className="px-2 py-3 font-semibold text-center">Payl.</th>
                  <th className="px-2 py-3 font-semibold text-center">Dışa Aktar</th>
                  <th className="px-2 py-3 font-semibold text-center">Tümü</th>
                  <th className="px-2 py-3 font-semibold text-center">Ata.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {modules.map((module) => (
                  <tr key={module.key} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{module.label}</td>
                    {[
                      'can_view', 'can_create', 'can_edit', 'can_delete',
                      'can_change_status', 'can_share', 'can_export',
                      'can_view_all', 'can_assign'
                    ].map(action => (
                      <td key={action} className="px-2 py-3 text-center">
                        {module.actions.includes(action) ? (
                          <button
                            onClick={() => togglePermission(activeDept, module.key, action)}
                            className={`p-1.5 rounded-md transition-colors ${permissions[activeDept]?.[module.key]?.[action]
                              ? 'bg-green-100 text-green-600'
                              : 'bg-gray-100 text-gray-400'
                              }`}
                          >
                            {permissions[activeDept]?.[module.key]?.[action] ? <Unlock size={14} /> : <Lock size={14} />}
                          </button>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev} icon={<ChevronLeft size={18} />}>Geri</Button>
        <Button onClick={handleSubmit} loading={loading} icon={<ChevronRight size={18} />} iconPosition="right">
          Kaydet & Devam Et
        </Button>
      </div>
    </div>
  );
};

/* Adım 5: Personeller */
const StepUsers = ({ departments, onNext, onPrev }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', password_confirmation: '', department_id: '', is_manager: false
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      showError('Kullanıcı listesi alınamadı.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        password: '',
        password_confirmation: '',
        department_id: user.department_id || '',
        is_manager: !!user.is_manager
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '', email: '', phone: '', password: '', password_confirmation: '', department_id: '', is_manager: false
      });
    }
    setIsModalOpen(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (formData.password && formData.password !== formData.password_confirmation) {
      showError('Şifreler eşleşmiyor.');
      return;
    }

    if (formData.password.length < 8) {
      showError('Şifre en az 8 karakter olmalıdır.');
      return;
    }

    setSubmitLoading(true);
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, formData);
        showSuccess('Kullanıcı güncellendi.');
      } else {
        await api.post('/users', formData);
        showSuccess('Kullanıcı eklendi.');
      }
      setIsModalOpen(false);
      fetchUsers();
    } catch (error) {
      showError(error.response?.data?.message || 'Kullanıcı kaydedilirken hata oluştu.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    const result = await showConfirm('Kullanıcı Silme', 'Bu personeli silmek istediğinize emin misiniz?');
    if (!result.isConfirmed) return;

    try {
      await api.delete(`/users/${id}`);
      showSuccess('Kullanıcı silindi.');
      fetchUsers();
    } catch (error) {
      showError(error.response?.data?.message || 'Kullanıcı silinemedi.');
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Personeller</h2>
        <Button size="sm" onClick={() => openModal()} icon={<Plus size={18} />}>Yeni Personel Ekle</Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        {loading ? (
          <div className="flex justify-center p-12"><Loader2 className="animate-spin text-primary-600" /></div>
        ) : (
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-6 py-4 font-semibold">Ad Soyad</th>
                <th className="px-6 py-4 font-semibold">E-posta</th>
                <th className="px-6 py-4 font-semibold">Departman</th>
                <th className="px-6 py-4 font-semibold text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {user.name}
                      {user.is_manager && <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase">Yön</span>}
                      {user.role === 'office_owner' && <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase">Sahip</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-500 text-sm">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">{user.department?.name || '—'}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openModal(user)} icon={<Pencil size={16} />} />
                      {user.role !== 'office_owner' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteUser(user.id)}
                          icon={<Trash2 size={16} />}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev} icon={<ChevronLeft size={18} />}>Geri</Button>
        <Button onClick={onNext} icon={<ChevronRight size={18} />} iconPosition="right">Devam Et</Button>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingUser ? 'Personel Düzenle' : 'Yeni Personel Ekle'}
      >
        <form onSubmit={handleSaveUser} className="space-y-4">
          <Input
            label="Ad Soyad"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <Input
            label="E-posta"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <Input
            label="Telefon"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <Select
            label="Departman"
            required
            value={formData.department_id}
            onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
            options={[
              { value: '', label: 'Departman Seçin' },
              ...departments.map(d => ({ value: d.id, label: d.name }))
            ]}
          />
          <div className="flex items-center gap-2 py-2">
            <input
              type="checkbox"
              id="is_manager"
              checked={formData.is_manager}
              onChange={(e) => setFormData({ ...formData, is_manager: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="is_manager" className="text-sm font-medium text-gray-700">Yönetici Yetkisi Ver</label>
          </div>
          <hr className="my-2" />
          <p className="text-xs text-gray-400">{editingUser ? 'Şifreyi değiştirmek istemiyorsanız boş bırakın.' : 'Personel için bir şifre belirleyin.'}</p>
          <Input
            label="Şifre"
            type="password"
            required={!editingUser}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          />
          <Input
            label="Şifre Tekrar"
            type="password"
            required={!editingUser}
            value={formData.password_confirmation}
            onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
          />
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>İptal</Button>
            <Button type="submit" loading={submitLoading}>Kaydet</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

/* Adım 6: Özet & Tamamla */
const StepSummary = ({ officeType, settings, departments, onPrev, refreshSetupStatus }) => {
  const [loading, setLoading] = useState(false);
  const [usersCount, setUsersCount] = useState(0);

  useEffect(() => {
    api.get('/users').then(res => setUsersCount(res.data.length));
  }, []);

  const handleComplete = async () => {
    setLoading(true);
    try {
      await api.post('/setup/complete');
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 }
      });
      showSuccess('Tebrikler! Ofis kurulumunuz başarıyla tamamlandı.');

      // Auth context yenile — isSetupCompleted true olacak, ProtectedRoute redirect edecek
      await refreshSetupStatus();

      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      showError(error.response?.data?.message || 'Kurulum tamamlanırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Kurulum Özeti</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card title="Ofis Amacı" icon={<Target size={20} className="text-primary-600" />}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center">
              {officeType === 'emlak_ofisi' ? <Building2 size={24} /> : <HardHat size={24} />}
            </div>
            <span className="font-bold text-gray-900 text-lg">
              {officeType === 'emlak_ofisi' ? 'Emlak Ofisi' : 'İnşaat Şirketi'}
            </span>
          </div>
        </Card>

        <Card title="Ofis Ayarları" icon={<Settings size={20} className="text-orange-500" />}>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex justify-between">
              <span>Veri Paylaşımı:</span>
              <span className="font-semibold">{settings.data_visibility === 'private' ? 'Gizli' : 'Şeffaf'}</span>
            </li>
            <li className="flex justify-between">
              <span>Portföy Görünürlüğü:</span>
              <span className="font-semibold">{settings.default_portfolio_visibility === 'owner' ? 'Sahibine Özel' : 'Tüm Ofise Açık'}</span>
            </li>
            <li className="flex justify-between">
              <span>Komisyon Payı:</span>
              <span className="font-semibold">%{settings.office_commission_rate}</span>
            </li>
          </ul>
        </Card>

        <Card title="Departmanlar" icon={<Building2 size={20} className="text-purple-500" />}>
          <div className="flex flex-wrap gap-2">
            {departments.map((dept, i) => (
              <span key={i} className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                {dept.name}
              </span>
            ))}
          </div>
          <p className="mt-4 text-xs text-gray-400">{departments.length} aktif departman yapılandırıldı.</p>
        </Card>

        <Card title="Personeller" icon={<Users size={20} className="text-blue-500" />}>
          <div className="flex items-center gap-3">
            <div className="text-3xl font-bold text-gray-900">{usersCount}</div>
            <div className="text-sm text-gray-500">Kayıtlı Personel</div>
          </div>
          <p className="mt-4 text-xs text-gray-400">Tüm personeller için varsayılan yetkiler atandı.</p>
        </Card>
      </div>

      <div className="bg-primary-50 border border-primary-100 rounded-2xl p-8 text-center mb-8">
        <h3 className="text-xl font-bold text-primary-900 mb-2">Her Şey Hazır!</h3>
        <p className="text-primary-700 mb-6">Kurulumu tamamladığınızda ofisiniz kullanıma açılacak ve tüm personeliniz sisteme giriş yapabilecektir.</p>
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <Button variant="outline" onClick={onPrev} className="md:w-48" icon={<ChevronLeft size={18} />}>Geri Dön</Button>
          <Button
            onClick={handleComplete}
            loading={loading}
            className="md:w-64 bg-green-600 hover:bg-green-700 border-none text-lg h-12 shadow-lg"
            icon={<CheckCircle size={20} />}
          >
            Kurulumu Tamamla
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
